import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@/core/Checkbox.atom';
import { FakeLink } from '@/core/FakeLink';
import { sqWorkbenchStore } from '@/core/core.stores';
import { useFlux } from '@/core/hooks/useFlux.hook';
import { changedDatasourcePreferenceProperties, trackUserProfileSave } from '@/header/userProfileTracking.utilities';
import { SEEQ_DATASOURCE } from '@/main/app.constants';
import { useSearchDatasources } from '@/search/search.store';
import { isAdmin } from '@/services/authorization.service';
import { setUserDatasources } from '@/workbench/workbench.actions';
import type { Datasource } from '@/search/search.types';

const getCheckboxBGColor = (isChecked: boolean) => {
  return isChecked ? 'sq-bg-included' : 'sq-bg-excluded';
};

/**
 * Tab to hide/show datasources based on user preferences; user can choose which
 * datasources show in their search filters. Each change is saved as soon as it is made.
 */
export const SaveDatasourcePreferencesTab = () => {
  const { t } = useTranslation();

  const datasources = useSearchDatasources();
  const { userDatasources, includeNewDatasources } = useFlux(sqWorkbenchStore);

  const allPossibleDatasources = _.chain(datasources)
    .reject(
      (datasource) =>
        datasource.datasourceClass === SEEQ_DATASOURCE.datasourceClass || datasource.scopedTo !== undefined,
    )
    .sortBy('name')
    .value();

  // Every change is saved straight away, so the saved preferences are what the tab shows. When newly-added
  // datasources are included, the saved list holds the datasources the user excluded.
  const isChecked = (datasource: Datasource): boolean =>
    includeNewDatasources ? !userDatasources.includes(datasource.id) : userDatasources.includes(datasource.id);
  const selectedDatasources = allPossibleDatasources.filter(isChecked);

  const saveSelection = (nextSelected: Datasource[], nextIncludeNew: boolean): void => {
    const updatedDatasources = nextIncludeNew
      ? datasources
          .filter(
            (datasource) => !nextSelected.some((selected) => selected.id === datasource.id) && !datasource.scopedTo,
          )
          .map((datasource) => datasource.id)
      : nextSelected.map((datasource) => datasource.id);
    const changedProperties = changedDatasourcePreferenceProperties(
      { datasourceIds: userDatasources, includeNewDatasources },
      { datasourceIds: updatedDatasources, includeNewDatasources: nextIncludeNew },
    );
    setUserDatasources(updatedDatasources, nextIncludeNew);
    trackUserProfileSave('Edit Datasource Preferences', changedProperties, 'success');
  };

  const handleSelectAllCheckbox = (): void =>
    saveSelection(selectedDatasources.length > 0 ? [] : allPossibleDatasources, includeNewDatasources);

  const handleSelectDatasource = (datasource: Datasource): void =>
    saveSelection(
      isChecked(datasource)
        ? selectedDatasources.filter((existing) => existing.id !== datasource.id)
        : [...selectedDatasources, datasource],
      includeNewDatasources,
    );

  const onExcludeModeChange = (includeNew: boolean): void => saveSelection(selectedDatasources, includeNew);

  const RadioGroup = (
    <div className="flexAlignLeft tw:mb-sq-10 tw:ml-sq-5 tw:pt-1">
      <div className="tw:mt-sq-10 tw:mb-sq-5">
        <b className="tw:mt-sq-5">{t('USER.DATASOURCE_PREFERENCES_NEWLY_ADDED')}</b>
      </div>
      <div className="form-check form-check-inline flexColumnContainer">
        <Checkbox
          id="datasource-preference-include"
          type="radio"
          onChange={() => onExcludeModeChange(true)}
          isChecked={includeNewDatasources}
          label={t('USER.DATASOURCE_INCLUDED_MODE')}
          classes="form-check-input"
        />
      </div>
      <div className="form-check form-check-inline flexColumnContainer">
        <Checkbox
          id="datasource-preference-exclude"
          type="radio"
          onChange={() => onExcludeModeChange(false)}
          isChecked={!includeNewDatasources}
          label={t('USER.DATASOURCE_EXCLUDED_MODE')}
          classes="form-check-input"
        />
      </div>
    </div>
  );

  return (
    <div className="flexRowContainer tw:overflow-hidden" data-testid="saveDatasourcePreferencesTab">
      <div className="flexColumnContainer tw:justify-between tw:pb-sq-10">
        <b className="tw:mb-sq-5">{t('USER.DATASOURCE_PREFERENCES_TITLE')}</b>
        {isAdmin() && (
          <FakeLink
            testId="manage-datasources"
            onClick={() => {
              window.open('/administration', 'SeeqAdmin');
            }}>
            {t('DATASOURCES.MANAGEMENT')}
          </FakeLink>
        )}
      </div>
      <div className="tw:mb-sq-5">{t('USER.DATASOURCE_PREFERENCES_DETAILS')}</div>
      <div className="tw:pl-sq-3">
        <Checkbox
          id="checkbox-toggle-all-datasources"
          label={t('USER.DATASOURCE_PREFERENCES_ALL_DATASOURCES_CHECKBOX_LABEL', {
            count: selectedDatasources.length,
            total: allPossibleDatasources.length,
          })}
          classes="pl2 pb5 pt5"
          isChecked={selectedDatasources.length > 0}
          onClick={() => handleSelectAllCheckbox()}
        />
      </div>
      <div className="overflowYScroll tw:mt-sq-5 tw:h-sq-200!">
        {allPossibleDatasources.map((datasource) => (
          <div key={datasource.id} className={`tw:pl-sq-3 ${getCheckboxBGColor(isChecked(datasource))}`}>
            <Checkbox
              id={`checkbox-${datasource.id}`}
              label={`${datasource.name}`}
              classes="l2 pb5 pt5"
              isChecked={isChecked(datasource)}
              onClick={() => handleSelectDatasource(datasource)}
            />
          </div>
        ))}
      </div>
      {RadioGroup}
    </div>
  );
};
